import { Component, ReactNode, ErrorInfo } from "react";
import { SkyButton, SkyMessageBar } from "@skypointcloud/skypoint-ui";
import { logger } from "../../services/logger.service";
import { reloadPage } from "./reloadPage";

interface RouteErrorBoundaryProps {
  children: ReactNode;
  fallbackTitle?: string;
  onReset?: () => void;
}

interface RouteErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  isChunkError: boolean;
  autoRetryCount: number;
  /** Recovering didn't work: show the card and leave the next reload to the user. */
  gaveUp: boolean;
}

const MAX_AUTO_RETRIES = 10;
const RETRY_BASE_DELAY = 200;

/**
 * At most one automatic reload per route in this window. A module that can't be
 * fetched (a stale dev-server dependency cache, a deploy that removed a chunk)
 * fails the same way after every reload, so reloading unconditionally turned one
 * broken import into a reload every ~3s, forever.
 */
const AUTO_RELOAD_WINDOW_MS = 30_000;
const AUTO_RELOAD_KEY = "skyhire:routeErrorBoundary:lastAutoReload";

/**
 * Claims this route's automatic reload: true when none happened for it in the
 * window. Without session storage there is no way to tell a loop, so no.
 */
function claimAutoReload(): boolean {
  const path = window.location.pathname;
  const now = Date.now();
  try {
    const last = JSON.parse(window.sessionStorage.getItem(AUTO_RELOAD_KEY) ?? "null") as {
      path?: string;
      at?: number;
    } | null;
    if (
      last?.path === path &&
      typeof last.at === "number" &&
      now - last.at < AUTO_RELOAD_WINDOW_MS
    ) {
      return false;
    }
    window.sessionStorage.setItem(AUTO_RELOAD_KEY, JSON.stringify({ path, at: now }));
    return true;
  } catch {
    return false;
  }
}

function isChunkLoadError(error: Error | null): boolean {
  if (!error) return false;
  const message = error.message?.toLowerCase() || "";
  const name = error.name?.toLowerCase() || "";
  return (
    message.includes("failed to fetch dynamically imported module") ||
    message.includes("loading chunk") ||
    message.includes("loading css chunk") ||
    message.includes("failed to load module script") ||
    message.includes("preload") ||
    name.includes("chunkloaderror") ||
    (error instanceof TypeError && message.includes("failed to fetch"))
  );
}

class RouteErrorBoundary extends Component<RouteErrorBoundaryProps, RouteErrorBoundaryState> {
  private refreshTimeout: ReturnType<typeof setTimeout> | null = null;
  private autoRetryTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(props: RouteErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      isChunkError: false,
      autoRetryCount: 0,
      gaveUp: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<RouteErrorBoundaryState> {
    return {
      hasError: true,
      error,
      isChunkError: isChunkLoadError(error),
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const isChunk = isChunkLoadError(error);

    logger.error("Route error caught by RouteErrorBoundary", error, {
      component: "RouteErrorBoundary",
      componentStack: errorInfo?.componentStack,
      isChunkError: isChunk,
    });

    if (isChunk) {
      this.reloadOnceOrGiveUp(1500);
    } else if (this.state.autoRetryCount < MAX_AUTO_RETRIES) {
      const delay = Math.min(RETRY_BASE_DELAY * Math.pow(1.5, this.state.autoRetryCount), 3000);
      this.autoRetryTimeout = setTimeout(() => {
        this.setState((prev) => ({
          hasError: false,
          error: null,
          isChunkError: false,
          autoRetryCount: prev.autoRetryCount + 1,
        }));
      }, delay);
    } else {
      this.reloadOnceOrGiveUp(2000);
    }
  }

  /** Reloads after `delay` if this route hasn't just been reloaded; otherwise the card. */
  private reloadOnceOrGiveUp(delay: number): void {
    if (claimAutoReload()) {
      this.refreshTimeout = setTimeout(reloadPage, delay);
    } else {
      this.setState({ gaveUp: true });
    }
  }

  componentWillUnmount(): void {
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
    }
    if (this.autoRetryTimeout) {
      clearTimeout(this.autoRetryTimeout);
    }
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null, isChunkError: false, autoRetryCount: 0 });
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  render(): ReactNode {
    if (this.state.hasError && this.state.gaveUp) {
      return (
        <div className="p-6">
          <SkyMessageBar
            intent="error"
            title={this.props.fallbackTitle ?? "This page couldn't load"}
            message="Reload to try again. If it keeps happening, contact your admin."
            actions={
              <SkyButton
                appearance="primary"
                onClick={reloadPage}
              >
                Reload
              </SkyButton>
            }
          />
        </div>
      );
    }

    if (this.state.hasError) {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            minHeight: "200px",
            width: "100%",
            opacity: 0.5,
          }}
        >
          <div
            style={{
              width: "24px",
              height: "24px",
              border: "3px solid #e5e7eb",
              borderTopColor: "#0078D4",
              borderRadius: "50%",
              animation: "reb-spin 0.8s linear infinite",
            }}
          />
          <style>{`@keyframes reb-spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      );
    }

    return this.props.children;
  }
}

export default RouteErrorBoundary;
